<template>
  <div class="shoes-list-background-wrapper">
    <div class="shoes-list-content">
      <div class="animate__animated animate__fadeInDown">
        <div class="shoes_title">
          <h2><span>精品搜尋</span></h2>
          <h3 class="ignore">Boutique Search</h3>
        </div>
      </div>
      <div class="animate__animated animate__fadeInDown">
        <div class="filter-container">
          <el-row :gutter="20" class="filter-inputs-row">
            <el-col :xs="24" :sm="12" :md="8">
              <el-select
                v-model="localSelectedGender"
                clearable
                placeholder="按性別篩選"
                style="width: 100%"
              >
                <el-option
                  v-for="item in GenderOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-col>

            <el-col :xs="24" :sm="12" :md="8">
              <el-select
                v-model="localSelectedKind"
                clearable
                placeholder="按種類篩選"
                style="width: 100%"
              >
                <el-option
                  v-for="item in AllKind"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="24" :md="8">
              <el-select
                v-model="localSelectedBrand"
                clearable
                placeholder="按品牌篩選"
                style="width: 100%"
                :loading="isLoadingBrands"
                loading-text="品牌加載中..."
                no-data-text="暫無品牌可選"
              >
                <el-option
                  v-for="item in brandOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                  class="ignore"
                />
              </el-select>
            </el-col>
          </el-row>

          <div class="action-buttons">
            <el-button type="primary" @click="applyFiltersAndEmit" :disabled="isSearchButtonDisabled">
              搜尋
            </el-button>
            <el-button @click="handleResetInputs">
              重置
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, defineEmits, watch, toRaw } from 'vue'; // 引入 toRaw
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router'; // 引入 useRoute
import { ElMessage } from 'element-plus';
import 'animate.css';
import frontServer from '../../services/FrontManage';

const store = useStore();
const router = useRouter();
const route = useRoute(); // 獲取當前路由
const bgImageUrl = ref('');
const emit = defineEmits(['trigger-fetch']);

// --- 本地状态管理筛选输入 ---
const localSelectedGender = ref('');
const localSelectedKind = ref('');
const localSelectedBrand = ref('');
const isLoadingBrands = ref(false); // 用於品牌加載狀態的新 ref

const GenderOptions = [
  {label: '男士',value: 'men'},
  {label: '女士',value:'female'},
];

const AllKind = [
  { label: '水槽&洗碗槽', value: 'sink_and_dishwasher' },
  { label: '花架&花盆', value: 'flower_stand_and_pot' },
  { label: '床頭靠墊/床頭軟包', value: 'bed_head_cushion' },
  { label: '山水畫', value: 'landscape_painting' },
  { label: '塌塌米豆帶沙發', value: 'tatami_sofa' },
  { label: '長凳子/長條椅', value: 'bench' },
  { label: '角鋼架/貨架', value: 'steel_shelf' },
  { label: '貓爬架/貓窩/貓屋', value: 'cat_tree_and_house' },
  { label: '戶外沙發', value: 'outdoor_sofa' },
  { label: '人台/服裝模特', value: 'mannequin' },
  { label: '蚊帳', value: 'mosquito_net' },
  { label: '健身器材', value: 'fitness_equipment' },
  { label: '衣櫥/衣櫃/衣架', value: 'wardrobe_and_rack' },
  { label: '鏡櫃', value: 'mirror_cabinet' },
  { label: '沙發/電腦椅/長椅', value: 'sofa_and_chair' },
];

// --- 计算属性用于禁用搜寻按钮 ---
const isSearchButtonDisabled = computed(() => {
  return !localSelectedKind.value;
});


const syncLocalFiltersWithStoreAndUrl = () => {
  const storeFilters = store.state.shoes.filters || {};
  const queryParams = route.query || {};

  // 優先使用 URL 查詢參數，其次是 store，最後默認為空
  localSelectedGender.value = queryParams.gender || storeFilters.gender || '';
  localSelectedKind.value = queryParams.kinds || storeFilters.kind || '';
  localSelectedBrand.value = queryParams.brands || storeFilters.brand || '';
};

const resetFilters = async () => {
  // Reset Vuex filters
  store.dispatch('shoes/updateFilters', { gender: '', brand: '', collection: ''});
};

onMounted(async () => {

  const baseConfig = await frontServer.GetFront();
  bgImageUrl.value = `${process.env.VUE_APP_BACKEND_BASE_URL}/${baseConfig.overImage}` || '';
  isLoadingBrands.value = true;
  try {
    // 获取品牌选项
    // 如果集合不存在或為空，則獲取集合
    if (!store.getters['shoes/allCollections'] || store.getters['shoes/allCollections'].length === 0) {
        await store.dispatch('shoes/fetchCollections');
    }
  } catch (error) {
    console.error("Failed to fetch collections on mount:", error);
    ElMessage.error('品牌列表加载失败，请稍后重试');
  } finally {
    isLoadingBrands.value = false;
  }
  // 獲取數據後同步本地狀態與 URL/Store
  syncLocalFiltersWithStoreAndUrl();
  resetFilters();
});

// 品牌选项，从 store.getters['shoes/allCollections'] 获取
const brandOptions = computed(() => {
  const collectionsFromStore = store.getters['shoes/allCollections'] || [];
  return collectionsFromStore.map(collection => ({
    label: collection.BrandName || collection.name, // 處理屬性名稱潛在的差異
    value: collection.BrandName || collection.name
  })).filter(option => option.label && option.value); // 確保沒有空的標籤/值
});

const applyFiltersAndEmit = async () => {
  if (isSearchButtonDisabled.value) {
    ElMessage.warning('搜寻条件为空，无法进行搜索。');
    return;
  }

  const filtersToApply = {
    gender: localSelectedGender.value || undefined,
    kind: localSelectedKind.value || undefined,
    brand: localSelectedBrand.value || undefined,
  };
  await store.dispatch('shoes/updateFilters', filtersToApply);

  const queryForUrl = {};
  if (filtersToApply.gender) queryForUrl.gender = filtersToApply.gender;
  if (filtersToApply.kind) queryForUrl.kinds = filtersToApply.kind;
  if (filtersToApply.brand) queryForUrl.brands = filtersToApply.brand;

  const newQuery = { ...route.query, ...queryForUrl, page: 1 }; // 新篩選時始終重置到第 1 頁

  // 如果鍵的值為 undefined（例如，已清除的篩選條件），則刪除它們
  if (!filtersToApply.gender) delete newQuery.gender;
  if (!filtersToApply.kind) delete newQuery.kinds;
  if (!filtersToApply.brand) delete newQuery.brands;

  // 僅當查詢實際更改時才推送，以避免冗餘導航
  if (JSON.stringify(toRaw(newQuery)) !== JSON.stringify(toRaw(route.query))) {
    router.push({ path: '/shoes/search', query: newQuery });
  } else {
    // 如果查詢相同，但篩選條件可能已重新應用（例如從空到特定）
    // 如果父組件邏輯依賴它，仍然發出事件以潛在地重新獲取
     emit('trigger-fetch', { resetPage: true });
  }
};

const handleResetInputs = async () => {
  localSelectedGender.value = '';
  localSelectedKind.value = '';
  localSelectedBrand.value = '';

};

watch(
  [localSelectedKind, localSelectedGender],
  async ([newKind, newGender], [oldKind, oldGender]) => {
    // 只有当任一值变化时才触发
    if (newKind !== oldKind || newGender !== oldGender) {
      isLoadingBrands.value = true;
      localSelectedBrand.value = ''; // 清空品牌选择

      try {
        await store.dispatch('shoes/fetchCollections', {
          kind: newKind || undefined,
          gender: newGender || undefined
        });
      } catch (error) {
        ElMessage.error('根据筛选条件获取品牌失败，请稍后重试');
      } finally {
        isLoadingBrands.value = false;
      }
    }
  }
);

// 監聽路由查詢更改（例如，瀏覽器後退/前進，直接修改 URL）
watch(() => route.query, async (newQuery, oldQuery) => {
  // 使用 toRaw 比較實際值，而不是代理對象
  if (JSON.stringify(toRaw(newQuery)) !== JSON.stringify(toRaw(oldQuery))) {

    // 1. 將本地輸入字段與新的 URL 查詢同步
    localSelectedGender.value = newQuery.gender || '';
    localSelectedKind.value = newQuery.kinds || '';
    localSelectedBrand.value = newQuery.brands || '';

    // 2. 使用來自 URL 的篩選條件更新 Vuex store
    const filtersFromUrl = {
      gender: newQuery.gender || undefined,
      kind: newQuery.kinds || undefined,
      brand: newQuery.brands || undefined,
    };

    // 檢查是否需要更新 store
    const currentStoreFilters = store.state.shoes.filters;
    let needsStoreUpdate = false;
    if (filtersFromUrl.gender !== currentStoreFilters.gender) needsStoreUpdate = true;
    if (filtersFromUrl.kind !== currentStoreFilters.kind) needsStoreUpdate = true;
    if (filtersFromUrl.brand !== currentStoreFilters.brand) needsStoreUpdate = true;
    
    if (needsStoreUpdate) {
        await store.dispatch('shoes/updateFilters', filtersFromUrl);
    }

    // 3. 發出事件以觸發父組件中的數據獲取
    // fromUrlChange 標誌可用於父組件了解來源
    emit('trigger-fetch', { resetPage: !newQuery.page || Number(newQuery.page) === 1, fromUrlChange: true });
  }
}, { deep: true, immediate: false }); // immediate: false，因為 onMounted 處理初始同步

</script>

<style scoped>
/* 样式与之前版本相同 */
.shoes-list-background-wrapper {
  min-height: 100vh;
  background-image: v-bind('`url(${bgImageUrl})`');
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: #e0e0e0;
  z-index: 1; /* 确保在 header 之下（如果header有更高z-index）*/
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  margin-top:-5.25%; /* 注意：这个负margin可能会导致与header的层叠问题 */
}

.shoes-list-content {
   max-width: 1200px;
   width: 100%;
   margin: 0 auto;
   padding: 20px;
   position: relative; /* 确保内部元素的z-index上下文 */
}

.filter-container {
  display: flex;
  align-items: flex-start; /* 將項目（行和按鈕）對齊到其起始位置 */
  gap: 15px;
  padding: 25px 30px;
  border-radius: 8px;
  backdrop-filter: blur(5px); /* 毛玻璃效果 */
  background-color: rgba(255, 255, 255, 0.15); /* 稍微不透明以提高可讀性 */
  position: relative;
  z-index: 1;
  margin: 0 auto;
}

.filter-inputs-row {
  flex-grow: 1; /* 允許輸入行佔用可用空間 */
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-shrink: 0; /* 防止按鈕收縮 */
  padding-top: 0; /* 如果 ElRow 沒有頂部填充，則與輸入字段的頂部對齊 */
  /* 如果 ElInput 有默認高度，並且您希望按鈕完美對齊，
     您可能需要確保按鈕和輸入的行高或明確高度使它們在視覺上對齊，或者調整 padding-top。
     Element Plus 通常能很好地處理這個問題。
  */
   white-space: nowrap; /* 防止按钮文字换行 */
}

.el-row {
  /* 默認的 gutter 會產生負邊距；如果父元素有內邊距，則可能不需要這些 */
  /* margin-left: 0 !important; */
  /* margin-right: 0 !important; */
}

.el-col {
  /* 默認的 gutter 會給列添加內邊距 */
  /* padding-left: 0 !important; */
  /* padding-right: 0 !important; */
}
/* 僅當 el-col 堆疊時（移動視圖）才為其添加 margin-bottom */
@media (max-width: 991.98px) { /* Bootstrap 的 md 斷點是 992px */
  .filter-container {
    flex-direction: column;
    align-items: stretch; /* 使子元素佔滿全部寬度 */
  }
  .filter-inputs-row .el-col {
      margin-bottom: 15px !important;
  }
   /* 堆疊時移除行中最後一個 el-col 的邊距 */
  .filter-inputs-row .el-col:last-child {
     /* margin-bottom: 15px !important; /* 這在操作按鈕之前導致了雙重邊距 */
  }
  .action-buttons {
    margin-top: 0; /* 如果最後一個 el-col 有 margin-bottom，則不需要額外的頂部邊距 */
    align-self: flex-start; /* 或者如果您希望按鈕佔滿全部寬度，則使用 'stretch' */
    width: auto; /* 或者如果 align-self 是 stretch 並且您希望按鈕佔滿全部寬度，則為 100% */
  }
}


@media (max-width: 767.98px) { /* Bootstrap 的 sm 斷點是 768px */
  .shoes-list-background-wrapper {
    min-height: auto; /* 允許內容定義高度 */
    /* 如果您有一個 72px 的固定頭部，請進行調整 padding-top: calc(72px + 20px); */
    padding-top: 20px; /* 更簡潔的內邊距 */
    padding-bottom: 30px;
    align-items: flex-start; /* 在小屏幕上將內容頂部對齊 */
    margin-top: 0; /* 在小屏幕上移除負邊距 */
  }
  .filter-container {
    margin-top: 0; /* 之前是 20px，如果 shoes-list-content 處理間距，則可以是 0 */
    padding: 20px;
    background-color: rgba(255, 255, 255, 0.3); /* 稍微不透明的背景 */
  }
   .filter-inputs-row .el-col:last-child {
      margin-bottom: 15px !important; /* 確保最後一個篩選項目在按鈕之前也有底部邊距 */
  }
}

.shoes_title {
  text-align: center;
  margin-bottom: 30px; /* 標題和篩選器之間的間距 */
}

.shoes_title h2 {
  font-size: clamp(3rem, 7vw, 6rem); /* 調整後的響應式字體大小 */
  margin: 0.5rem 0;
  font-family: 'Helvetica Neue', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-weight: 600;
  letter-spacing: 1.5px;
  background: linear-gradient(15deg, #D32F2F 25%, #9A0000 75%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.1); /* 細微的陰影以增加深度感 */
}

.shoes_title h3 {
  font-size: clamp(1.8rem, 4vw, 3.5rem); /* 調整後的響應式字體大小 */
  margin: 0.5rem auto 0 auto;
  font-family: 'Arial', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.5px;
  opacity: 0.85;
  background: linear-gradient(15deg, #D32F2F 25%, #9A0000 75%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
}

@media (max-width: 575.98px) { /* Bootstrap 的 xs 斷點是 576px */
  .filter-container {
     padding: 15px;
     background-color: rgba(255, 255, 255, 0.5); /* 在非常小的屏幕上更加不透明 */
  }
  .action-buttons {
    flex-direction: column; /* 垂直堆疊按鈕 */
    align-items: stretch;   /* 使按鈕佔滿全部寬度 */
  }
  .action-buttons .el-button {
    width: 100%;
    margin-left: 0 !important; /* 移除 Element Plus 默認的左邊距 */
  }
  .action-buttons .el-button + .el-button {
    margin-top: 8px; /* 堆疊按鈕之間的間距 */
  }
  .shoes_title h2 {
    font-size: clamp(2.2rem, 8vw, 3.5rem);
  }
  .shoes_title h3 {
    font-size: clamp(1.3rem, 6vw, 2.2rem);
  }
}
</style>